import type { FC, ReactNode } from "react";
import { Modal } from "flowbite-react";
import { Button } from "@components";
import {
  highlightBox,
  modalFooter,
  textMuted,
  textHeading,
} from "@design-system";
import { modalPortalRoot } from "../../helpers/modalRoot";

export interface ConfirmDeleteModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  description: ReactNode;
  /** Highlighted name shown in the emphasised box (e.g. the category name). */
  highlightedName: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
}

/**
 * Shared confirmation modal for destructive actions. Consolidates the
 * previously duplicated delete-category modals in ProductCategories and
 * VehicleCategories; rendered output is identical to those implementations.
 */
export const ConfirmDeleteModal: FC<ConfirmDeleteModalProps> = ({
  show,
  onClose,
  onConfirm,
  title,
  description,
  highlightedName,
  cancelLabel,
  confirmLabel,
}) => {
  return (
    <Modal
      root={modalPortalRoot}
      show={show}
      onClose={onClose}
      size="md"
      aria-labelledby="delete-category-modal"
      theme={{
        root: {
          base: "fixed top-0 right-0 left-0 z-50 h-modal h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          show: {
            on: "flex bg-gray-900 bg-opacity-70 dark:bg-opacity-90",
            off: "hidden",
          },
        },
        content: {
          base: "relative h-full w-full p-2 sm:p-4 md:h-auto",
          inner:
            "relative rounded-lg bg-white shadow-lg dark:bg-surface border dark:border-gray-700 flex flex-col max-h-[95vh] sm:max-h-[90vh] shadow-xl dark:shadow-gray-900/50",
        },
      }}
    >
      <Modal.Header
        id="delete-category-modal"
        className="border-b border-border"
      >
        {title}
      </Modal.Header>
      <Modal.Body className="dark:bg-surface">
        <div className="space-y-6">
          <p className={`text-base leading-relaxed ${textMuted}`}>
            {description}
          </p>
          <div className={highlightBox}>
            <p className={`text-center font-medium ${textHeading}`}>
              {highlightedName}
            </p>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className={modalFooter}>
        <Button color="gray" onClick={onClose} size="sm">
          {cancelLabel}
        </Button>
        <Button color="failure" onClick={onConfirm} size="sm">
          {confirmLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
